import PropTypes from 'prop-types';
import {
	BsArrowRight,
	BsCheckCircleFill,
	BsThreeDots,
	BsPencil,
	BsTrash,
	BsCheck,
	BsArrowCounterclockwise,
	BsEnvelope,
	BsEyeFill,
	BsBoxArrowUpRight,
	BsExclamationTriangleFill,
	BsQrCode,
	BsXLg,
} from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import IconButton from '#globalComponents/IconButton/IconButton';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

function formatDateTime(iso) {
	if (!iso) return '';
	try {
		return new Intl.DateTimeFormat('de-DE', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(new Date(iso));
	} catch {
		return '';
	}
}

const EDITABLE_TYPES = new Set(['note', 'communication', 'task', 'meeting']);

function buildCardProps(canEdit, onClickEdit) {
	if (!canEdit) {
		return {
			className: 'p-4',
		};
	}
	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onClickEdit();
		}
	};
	return {
		className: 'p-4 hover:border-primary-200 transition-colors cursor-pointer',
		role: 'button',
		tabIndex: 0,
		onClick: onClickEdit,
		onKeyDown: handleKeyDown,
	};
}

const ActivityHistoryItem = ({
	activity: a,
	channelType: ch,
	Icon,
	dotClass,
	isCompletedTask,
	isImmutable,
	isCampaign,
	canReopen,
	when,
	historyMenuId,
	setHistoryMenuId,
	onEdit,
	onReopen,
	onDelete,
	renderInlineEditForm,
	isEditing,
}) => {
	const sc = a.activityType === 'stage_change';
	// Channels whose history entries represent an actual dispatch (email/letter sent) to mark them as non-editable
	const SENT_CHANNEL_SLUGS = new Set(['email', 'letter']);
	const isSentMessage = a.activityType === 'communication' && SENT_CHANNEL_SLUGS.has(ch?.slug);
	const canEdit = !isCampaign && !isImmutable && !isSentMessage && EDITABLE_TYPES.has(a.activityType);

	if (isEditing) {
		return (
			<div className="relative">
				<div
					className={`absolute -left-6 top-2.5 w-6 h-6 rounded-full border-2 z-10 grid place-items-center ${dotClass}`}
				>
					{isCompletedTask ? <BsCheck className="w-3 h-3" /> : <Icon className="w-2.5 h-2.5" />}
				</div>
				{renderInlineEditForm(a)}
			</div>
		);
	}

	return (
		<div className="relative group">
			<div
				className={`absolute -left-6 top-2.5 w-6 h-6 rounded-full border-2 z-10 grid place-items-center ${dotClass}`}
			>
				{isCompletedTask ? <BsCheck className="w-3 h-3" /> : <Icon className="w-2.5 h-2.5" />}
			</div>
			<Card {...buildCardProps(canEdit, () => onEdit(a))}>
				<div className="flex items-start gap-2">
					<div className="flex-1 min-w-0">
						{sc && a.channelPayload?.fromStageName && a.channelPayload?.toStageName ? (
							<div className="flex items-center gap-1.5 text-md font-semibold">
								<span className="text-text">{a.channelPayload.fromStageName}</span>
								<BsArrowRight className="w-3 h-3 text-bgBase-800 flex-shrink-0" />
								<span className="text-primary-600 font-semibold">{a.channelPayload.toStageName}</span>
							</div>
						) : isCompletedTask ? (
							<div className="flex items-center gap-1.5 flex-wrap">
								<span className="text-md font-medium text-bgBase-800 line-through decoration-bgBase-600">
									{a.title}
								</span>
								<span className="inline-block text-[9px] font-semibold px-1.5 py-px rounded-full bg-success-200 text-success-700 uppercase tracking-wide">
									erledigt
								</span>
							</div>
						) : (
							<div className="flex items-center gap-1.5 flex-wrap">
								<span className="text-md font-semibold truncate text-text">{a.title}</span>
								{a.channelPayload?.reached === true && (
									<span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-success-200 text-success-700 font-medium">
										<BsCheckCircleFill className="w-2.5 h-2.5" /> Erreicht
									</span>
								)}
								{a.channelPayload?.reached === false && (
									<span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-caution-200 text-caution-700 font-medium">
										<BsXLg className="w-2 h-2" /> Nicht erreicht
									</span>
								)}
							</div>
						)}
					</div>
					<div
						className="flex items-center gap-1.5 flex-shrink-0 text-xs text-text"
						onClick={(e) => e.stopPropagation()}
					>
						<span>{formatDateTime(when)}</span>
						{!isImmutable && (
							<Popover
								open={historyMenuId === a.id}
								onClose={() => setHistoryMenuId(null)}
								size="s"
								align="end"
								trigger={
									<IconButton
										icon={BsThreeDots}
										label="Aktionen"
										onClick={() => setHistoryMenuId(historyMenuId === a.id ? null : a.id)}
										className="h-6 w-6 opacity-60 group-hover:opacity-100 transition-[color,background-color,opacity]"
									/>
								}
							>
								<Panel.Body padded={false}>
									<Menu>
										{canReopen && (
											<Menu.Item
												icon={BsArrowCounterclockwise}
												onClick={() => {
													onReopen(a);
													setHistoryMenuId(null);
												}}
											>
												Wieder öffnen
											</Menu.Item>
										)}
										{canEdit && (
											<Menu.Item icon={BsPencil} onClick={() => onEdit(a)}>
												Bearbeiten
											</Menu.Item>
										)}
										<Menu.Separator />
										<Menu.Item
											icon={BsTrash}
											tone="danger"
											onClick={() => {
												onDelete(a);
												setHistoryMenuId(null);
											}}
										>
											Löschen
										</Menu.Item>
									</Menu>
								</Panel.Body>
							</Popover>
						)}
					</div>
				</div>
				{a.channelPayload?.recipient && (
					<p className="text-xs text-text mt-1 flex items-center gap-1">
						<BsEnvelope className="w-2.5 h-2.5" /> An: {a.channelPayload.recipient}
					</p>
				)}
				{a.channelPayload?.direction &&
					a.channelPayload?.reached === null &&
					ch?.slug === 'email' &&
					!a.channelPayload?.recipient && (
						<p className="text-xs text-text mt-1">
							{a.channelPayload.direction === 'inbound' ? 'Eingehend' : 'Ausgehend'}
						</p>
					)}
				{ch?.slug === 'email' && a.channelPayload?.openedAt && (
					<p className="text-xs text-text mt-1 flex items-center gap-1">
						<BsEyeFill className="w-2.5 h-2.5" /> Geöffnet: {formatDateTime(a.channelPayload.openedAt)}
					</p>
				)}
				{ch?.slug === 'email' && a.channelPayload?.clickedAt && (
					<p className="text-xs text-text mt-1 flex items-center gap-1">
						<BsBoxArrowUpRight className="w-2.5 h-2.5" /> Geklickt:{' '}
						{formatDateTime(a.channelPayload.clickedAt)}
					</p>
				)}
				{ch?.slug === 'letter' && a.channelPayload?.qrScannedAt && (
					<p className="text-xs text-text mt-1 flex items-center gap-1">
						<BsQrCode className="w-2.5 h-2.5" /> QR gescannt: {formatDateTime(a.channelPayload.qrScannedAt)}
					</p>
				)}
				{ch?.slug === 'email' && a.status === 'failed' && (
					<p className="text-xs text-warning-500 mt-1 flex items-center gap-1">
						<BsExclamationTriangleFill className="w-2.5 h-2.5" /> Fehlgeschlagen
						{a.channelPayload?.errorMessage && (
							<span className="text-warning-500">– {a.channelPayload.errorMessage}</span>
						)}
					</p>
				)}
				{a.body && (
					<p className="text-xs text-text mt-1 line-clamp-3 whitespace-pre-wrap leading-relaxed">{a.body}</p>
				)}
			</Card>
		</div>
	);
};

ActivityHistoryItem.propTypes = {
	activity: PropTypes.object.isRequired,
	channelType: PropTypes.object,
	Icon: PropTypes.elementType.isRequired,
	dotClass: PropTypes.string.isRequired,
	isCompletedTask: PropTypes.bool.isRequired,
	isImmutable: PropTypes.bool.isRequired,
	isCampaign: PropTypes.bool.isRequired,
	canReopen: PropTypes.bool.isRequired,
	when: PropTypes.string,
	historyMenuId: PropTypes.string,
	setHistoryMenuId: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onReopen: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	renderInlineEditForm: PropTypes.func.isRequired,
	isEditing: PropTypes.bool.isRequired,
};

export default ActivityHistoryItem;
